/**
 * `createStaticView` — build a virtual view backed by fixed in-memory rows
 * rather than a source CDF view. The catalog uses it to surface static
 * reference tables (lookup lists, enumerations, fixed registries) to the
 * agent: the rows are authored declaratively and served directly, with no
 * `CogniteClient` and no per-row timeseries minting.
 *
 * The column list is the SSOT: the engine `Schema` and the agent-facing
 * `ViewDescription` are both derived from it. Row materialisation, the leaf
 * `fetch`, and the residual filter / sort / limit replay are all delegated to
 * the shared in-memory leaf provider (`createInMemoryProvider`); this factory
 * only adds the virtual-view shell (describe / claims) and a synthetic
 * instance-identity axis so set-refs over the view recover a stable
 * `{ space, externalId }` per row.
 */

import type { SourceFetcherCtx } from '#core/lib/expr/EvalContext.ts';
import type { AstInput } from '#core/lib/expr/normalize.ts';
import type { PlanNode } from '#core/lib/expr/plan.ts';
import { createInMemoryProvider, type InMemoryTable } from '#core/lib/expr/providers/in-memory.ts';
import { type PipelineHooks, parseSourcePipeline } from '#core/lib/expr/providers/pipeline.ts';
import type { DecodedParams, PlanCtx, ProviderSource } from '#core/lib/expr/providers/types.ts';
import {
  type ColumnDoc,
  type ColumnType,
  projectSchema,
  type Schema,
  schemaFromEntries,
  type ViewDescription,
} from '#core/lib/expr/schema/types.ts';
import { type Cell, MaterializedDataset, type RowIdentity } from '#core/lib/expr/value.ts';

import { makeVirtualViewProviderShell, type VirtualView } from './createReshapedView.ts';

/** One typed column of a static reference table. */
export type StaticColumn = {
  /** Canonical column name, as it appears in every row record and to the agent. */
  readonly name: string;
  /** Engine column type carried by this column. */
  readonly type: ColumnType;
  /** Agent-facing description of the column. */
  readonly description: string;
};

/** Declarative spec for a static reference-table view. */
export type StaticViewSpec = {
  /** Agent-facing registry name. */
  readonly name: string;
  /** View-level description shown to the agent by the DescribeViews tool. */
  readonly description: string;
  /** The view's typed columns — the SSOT for both the engine schema and the agent docs. */
  readonly columns: readonly StaticColumn[];
  /** The fixed rows backing the view, keyed by canonical column name. */
  readonly rows: readonly Readonly<Record<string, Cell>>[];
};

/**
 * The space all synthesised static-view identities live in. A static view is
 * not instance-backed, so the axis is purely synthetic — a stable
 * `{ space, externalId }` per row index keeps set-refs over the view from
 * failing on a missing identity axis.
 */
const STATIC_VIEW_SPACE = 'static-view';

/**
 * A static view pushes nothing down — every constraint replays client-side
 * over the materialised rows. Identical in spirit to the query op's
 * non-pushing leaf hooks: no leaf is pushable, `translate` is the no-op the
 * partition contract requires, and no non-predicate constraint is absorbed.
 */
const NON_PUSHING_HOOKS: PipelineHooks<AstInput> = {
  isPushable: () => false,
  translate: (node) => node,
  absorbableConstraints: [],
};

export const createStaticView = (spec: StaticViewSpec): VirtualView => {
  // The column list is the single source of truth: the engine schema and the
  // agent docs are both derived from it.
  const declared: Schema = schemaFromEntries(spec.columns.map((c: StaticColumn) => [c.name, c.type] as const));
  const viewDoc: ViewDescription = {
    description: spec.description,
    columns: spec.columns.map(
      (c: StaticColumn): ColumnDoc => ({ name: c.name, type: c.type, description: c.description }),
    ),
  };

  // Delegate row materialisation + fetch to the shared in-memory leaf
  // provider — this factory never reimplements row→column projection.
  const leaf = createInMemoryProvider(`virtual-view:${spec.name}:rows`, {
    [spec.name]: { schema: declared, rows: spec.rows } satisfies InMemoryTable,
  });
  const leafFetch = leaf.fetch;
  if (leafFetch === undefined) {
    throw new Error('in-memory leaf provider must define fetch');
  }

  // Synthetic instance identity: one stable `{ space, externalId }` per row
  // index, so set-refs over the view recover a full identity rather than
  // failing on a missing axis.
  const identity: readonly RowIdentity[] = spec.rows.map((_row: Readonly<Record<string, Cell>>, index: number) => ({
    space: STATIC_VIEW_SPACE,
    externalId: `${spec.name}:${index}`,
  }));

  const shell = makeVirtualViewProviderShell({ name: spec.name, schema: declared, viewDoc });

  return {
    name: spec.name,
    provider: {
      ...shell,

      plan: (_name: string, params: DecodedParams, _ctx: PlanCtx) => {
        // Nothing pushes: the whole filter / sort / limit / columns pipeline
        // becomes residual ops replayed over the static surface, so the
        // engine handles them uniformly. Declared == fetched, so the
        // size-strict refinement post-condition holds after residual replay.
        const parsed = parseSourcePipeline(params, declared, NON_PUSHING_HOOKS);
        const source: ProviderSource = { kind: 'provider', providerId: shell.id, native: {} };
        const residual: readonly PlanNode[] = parsed.residualOps;
        return { source, residual, schema: projectSchema(declared, params.columns) };
      },

      fetch: async (_source: ProviderSource, ctx: SourceFetcherCtx): Promise<MaterializedDataset> => {
        // Reuse the in-memory leaf's row materialisation, then rebuild with
        // the synthetic identity axis a static view exposes.
        const leafSource: ProviderSource = { kind: 'provider', providerId: leaf.id, native: { name: spec.name } };
        const materialized = await leafFetch(leafSource, ctx);
        return new MaterializedDataset(materialized.columns, declared, identity);
      },
    },
  };
};
